(() => {
  if (window.__praCepCalculatorLoaded) return;
  window.__praCepCalculatorLoaded = true;

  const STORAGE_KEY = "pra:delivery-address:v1";
  const UPDATE_EVENT = "pra:cep:update";

  const digitsOnly = (value) =>
    String(value || "")
      .replace(/\D/g, "")
      .slice(0, 8);
  const formatCep = (value) => {
    const digits = digitsOnly(value);
    return digits.length > 5
      ? `${digits.slice(0, 5)}-${digits.slice(5)}`
      : digits;
  };

  const readAddress = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return value && /^\d{8}$/.test(value.cep) ? value : null;
    } catch (error) {
      return null;
    }
  };

  const saveAddress = (address) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(address));
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: address }));
  };

  const destinationLabel = (address) => {
    if (!address) return "";
    const locality = [address.city, address.state].filter(Boolean).join(" — ");
    return locality || formatCep(address.cep);
  };

  const REGION_BY_STATE = {
    PR: "south",
    SC: "south",
    RS: "south",
    SP: "southeast",
    RJ: "southeast",
    MG: "southeast",
    ES: "southeast",
    DF: "midwest",
    GO: "midwest",
    MT: "midwest",
    MS: "midwest",
    AL: "northeast",
    BA: "northeast",
    CE: "northeast",
    MA: "northeast",
    PB: "northeast",
    PE: "northeast",
    PI: "northeast",
    RN: "northeast",
    SE: "northeast",
    AC: "north",
    AM: "north",
    AP: "north",
    PA: "north",
    RO: "north",
    RR: "north",
    TO: "north",
  };

  const DEFAULT_STATE_RATES = {
    PR: "16|18|21|2-4",
    SC: "19|21|24|3-5",
    RS: "21|23|27|3-6",
    SP: "20|22|25|3-5",
    RJ: "22|24|28|4-6",
    MG: "21|23|27|4-6",
    ES: "22|24|29|4-6",
    DF: "23|26|31|4-7",
    GO: "26|29|35|5-8",
    MS: "27|31|37|5-9",
    MT: "28|32|38|6-9",
    BA: "32|36|48|7-11",
    CE: "33|37|49|7-12",
    PE: "32|36|48|7-12",
    PB: "34|39|51|8-12",
    RN: "34|39|51|8-12",
    AL: "35|40|52|8-13",
    SE: "35|40|52|8-13",
    PI: "36|41|53|8-13",
    MA: "37|42|54|9-14",
    AM: "39|45|58|9-15",
    PA: "38|44|57|9-15",
    RR: "41|47|60|10-16",
    AP: "42|48|61|10-16",
    AC: "43|49|62|10-17",
  };

  const BLOCKED_CEPS = new Set(["53990000"]);
  const WEIGHT_TIERS = [
    { maxGrams: 500, key: "Light", label: "até 0,5 kg" },
    { maxGrams: 1000, key: "Medium", label: "de 0,501 a 1 kg" },
    {
      maxGrams: Number.POSITIVE_INFINITY,
      key: "Heavy",
      label: "faixa de 2 kg (1,001 kg ou mais)",
    },
  ];

  const parseNumberRange = (value) => {
    const [minimum, maximum] = String(value || "")
      .split("-")
      .map((item) => Number(item.trim().replace(",", ".")));
    if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) return null;
    return { minimum, maximum };
  };

  const buildStateRateTable = (element) => {
    const table = {};
    Object.entries(DEFAULT_STATE_RATES).forEach(([state, fallback]) => {
      const row = element.dataset[`stateRate${state}`] || fallback;
      const [light, medium, heavy, transit] = row
        .split("|")
        .map((item) => item.trim());
      const lightPrice = Number(light?.replace(",", "."));
      const mediumPrice = Number(medium?.replace(",", "."));
      const heavyPrice = Number(heavy?.replace(",", "."));
      const transitRange = parseNumberRange(transit);
      if (
        !Number.isFinite(lightPrice) ||
        !Number.isFinite(mediumPrice) ||
        !Number.isFinite(heavyPrice) ||
        !transitRange
      )
        return;
      table[state] = {
        region: REGION_BY_STATE[state],
        prices: {
          Light: lightPrice,
          Medium: mediumPrice,
          Heavy: heavyPrice,
        },
        transit: transitRange,
      };
    });
    return table;
  };

  const formatWeight = (grams) => {
    const kilograms = Number(grams) / 1000;
    return `${new Intl.NumberFormat("pt-BR", {
      maximumFractionDigits: 3,
    }).format(kilograms)} kg`;
  };

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let remaining = Math.max(0, Number(days) || 0);
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) remaining -= 1;
    }
    return result;
  };

  const formatDateRange = (start, end) => {
    const formatter = new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "short",
    });
    return `${formatter.format(start).replace(".", "")} a ${formatter.format(end).replace(".", "")}`;
  };

  const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  };

  const upsertCheckoutField = (form, name, value) => {
    if (!value) return;
    let field = form.querySelector(
      `input[data-pra-checkout-field][name="${name}"]`,
    );
    if (!field) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = name;
      field.dataset.praCheckoutField = "";
      form.appendChild(field);
    }
    field.value = value;
  };

  const applyCheckoutAddress = (address) => {
    if (!address) return;
    document
      .querySelectorAll('form[action$="/cart"], form[action="/cart"]')
      .forEach((form) => {
        upsertCheckoutField(
          form,
          "checkout[shipping_address][zip]",
          formatCep(address.cep),
        );
        upsertCheckoutField(
          form,
          "checkout[shipping_address][city]",
          address.city,
        );
        upsertCheckoutField(
          form,
          "checkout[shipping_address][province]",
          address.state,
        );
        upsertCheckoutField(form, "checkout[shipping_address][country]", "BR");
        upsertCheckoutField(
          form,
          "checkout[shipping_address][address1]",
          address.street,
        );
        upsertCheckoutField(
          form,
          "checkout[shipping_address][address2]",
          address.neighborhood,
        );
      });
  };

  const updateCartSummaries = (address, quote = null) => {
    document.querySelectorAll('.pra-v2-cart-cep-note').forEach((note) => {
      note.hidden = Boolean(address);
    });
    document.querySelectorAll("[data-pra-cart-shipping]").forEach((summary) => {
      const destination = summary.querySelector(
        "[data-pra-shipping-destination]",
      );
      const value = summary.querySelector("[data-pra-shipping-value]");
      if (destination) {
        destination.textContent = address
          ? `CEP ${formatCep(address.cep)} · ${destinationLabel(address)}. Endereço e disponibilidade serão confirmados no checkout.`
          : "Informe seu CEP para identificar o estado de entrega.";
      }
      if (value)
        value.textContent = quote?.displayPrice || "Calculado no checkout";
    });
  };

  if (!customElements.get("pra-cep-calculator")) {
    class PraCepCalculator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          document.addEventListener(UPDATE_EVENT, this.handleAddressUpdate);
          this.connectProductListeners();
          return;
        }
        this.form = this.querySelector("[data-cep-form]");
        this.input = this.querySelector("[data-cep-input]");
        this.message = this.querySelector("[data-cep-message]");
        this.quote = this.querySelector("[data-cep-quote]");
        this.submitButton = this.querySelector("[data-cep-submit]");
        this.editButton = this.querySelector("[data-cep-edit]");
        if (!this.form || !this.input || !this.message) return;
        this.initialized = true;
        this.stateRates = buildStateRateTable(this);
        this.productInfo = this.closest("product-info");
        this.quantityInput = this.productInfo?.querySelector(
          'input[name="quantity"]',
        );
        this.handleQuantityChange = () => this.refreshManualQuote();
        this.handleVariantChange = (event) => {
          const variant = event?.data?.variant;
          if (!variant || !this.productInfo) return;
          if (
            event.data.sectionId &&
            event.data.sectionId !== this.productInfo.dataset.section
          )
            return;
          this.dataset.unitWeightGrams = String(variant.weight || 0);
          this.dataset.variantId = String(variant.id || "");
          this.refreshManualQuote();
        };
        this.connectProductListeners();

        const saved = readAddress();
        if (saved) {
          this.input.value = formatCep(saved.cep);
          applyCheckoutAddress(saved);
          this.showAddress(saved, true);
        }

        this.input.addEventListener("input", () => {
          this.input.value = formatCep(this.input.value);
          this.input.removeAttribute("aria-invalid");
          this.message.hidden = true;
          if (this.quote) this.quote.hidden = true;
          delete this.dataset.state;
          this.updateSubmitState();
        });

        this.form.addEventListener("submit", (event) => {
          event.preventDefault();
          this.lookup();
        });

        this.editButton?.addEventListener("click", () => {
          this.dataset.editing = "true";
          this.editButton.hidden = true;
          this.form.hidden = false;
          this.input.focus();
          this.input.select();
        });

        this.handleAddressUpdate = (event) => {
          if (
            !event.detail ||
            event.detail.cep === digitsOnly(this.input.value)
          )
            return;
          this.input.value = formatCep(event.detail.cep);
          this.showAddress(event.detail, true);
        };
        document.addEventListener(UPDATE_EVENT, this.handleAddressUpdate);

        this.updateSubmitState();
      }

      disconnectedCallback() {
        document.removeEventListener(UPDATE_EVENT, this.handleAddressUpdate);
        this.quantityInput?.removeEventListener(
          "change",
          this.handleQuantityChange,
        );
        this.quantityInput?.removeEventListener("input", this.handleQuantityChange);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
      }

      connectProductListeners() {
        if (this.dataset.context !== "product") return;
        this.quantityInput?.removeEventListener(
          "change",
          this.handleQuantityChange,
        );
        this.quantityInput?.removeEventListener("input", this.handleQuantityChange);
        this.quantityInput?.addEventListener("change", this.handleQuantityChange);
        this.quantityInput?.addEventListener("input", this.handleQuantityChange);
        if (
          !this.variantChangeUnsubscriber &&
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange,
          );
        }
      }

      refreshManualQuote() {
        if (this.dataset.mode !== "manual_regions") return;
        const address = readAddress();
        if (address) this.showAddress(address, true);
      }

      updateSubmitState() {
        if (!this.submitButton || !this.input) return;
        const isComplete = /^\d{8}$/.test(digitsOnly(this.input.value));
        this.submitButton.disabled = Boolean(this.isLoading) || !isComplete;
      }

      async lookup() {
        const cep = digitsOnly(this.input.value);
        if (!/^\d{8}$/.test(cep)) {
          this.showError("Digite um CEP válido com 8 números.");
          return;
        }

        this.setLoading(true);
        try {
          const response = await fetchWithTimeout(
            `https://viacep.com.br/ws/${cep}/json/`,
          );
          if (!response.ok) throw new Error("cep-request-failed");
          const data = await response.json();
          if (data.erro) {
            this.showError(
              "CEP não encontrado. Confira os números e tente novamente.",
            );
            return;
          }

          const address = {
            cep,
            formattedCep: formatCep(cep),
            street: data.logradouro || "",
            neighborhood: data.bairro || "",
            city: data.localidade || "",
            state: data.uf || "",
            ibge: data.ibge || "",
          };
          if (BLOCKED_CEPS.has(address.cep)) {
            this.showError(
              "Não realizamos entregas para Fernando de Noronha (CEP 53990-000).",
            );
            return;
          }
          saveAddress(address);
          applyCheckoutAddress(address);
          await this.showAddress(address, true);
        } catch (error) {
          this.showError(
            "Não foi possível consultar o CEP agora. Tente novamente em instantes.",
          );
        } finally {
          this.setLoading(false);
        }
      }

      async showAddress(address, requestQuote) {
        if (BLOCKED_CEPS.has(address.cep)) {
          this.showError(
            "Não realizamos entregas para Fernando de Noronha (CEP 53990-000).",
          );
          return;
        }
        this.dataset.state = "success";
        delete this.dataset.editing;
        const isProductCalculator = this.dataset.context === "product";
        this.form.hidden = isProductCalculator ? false : true;
        if (this.editButton) this.editButton.hidden = isProductCalculator;
        this.input.removeAttribute("aria-invalid");
        this.message.hidden = false;
        this.message.textContent = `CEP reconhecido: ${destinationLabel(address)}. Frete e prazo serão confirmados no checkout.`;
        if (this.dataset.context === "cart") updateCartSummaries(address);

        if (requestQuote && this.dataset.mode === "manual_regions") {
          this.loadManualStateQuote(address);
        } else if (
          requestQuote &&
          this.dataset.mode === "live" &&
          this.dataset.quoteEndpoint
        ) {
          await this.loadQuote(address);
        } else if (this.quote) {
          this.quote.hidden = true;
        }
      }

      loadManualStateQuote(address) {
        const region = REGION_BY_STATE[address.state];
        const stateRate = this.stateRates[address.state];
        if (!region || !stateRate) {
          const unavailableState = ["RO", "TO"].includes(address.state);
          this.showError(
            unavailableState
              ? `A tabela J&T fornecida não contém tarifa para ${address.state}. O frete não pode ser calculado sem uma cotação confirmada.`
              : "Ainda não temos uma regra de entrega para este CEP.",
          );
          return;
        }

        const weightGrams = this.getQuoteWeightGrams();
        if (!Number.isFinite(weightGrams) || weightGrams <= 0) {
          this.showError(
            "O peso deste produto ainda não foi cadastrado. Cadastre o peso no Shopify para calcular o frete.",
          );
          return;
        }
        const weightTier = WEIGHT_TIERS.find(
          (tier) => weightGrams <= tier.maxGrams,
        );
        const price = stateRate.prices[weightTier.key];
        const transitMin = stateRate.transit.minimum;
        const transitMax = stateRate.transit.maximum;
        const productionMin = Number(this.dataset.productionMin || 0);
        const productionMax = Number(this.dataset.productionMax || 0);
        if (!Number.isFinite(price) || price < 0 || !transitMin || !transitMax) {
          this.showError(
            "A regra de frete deste estado ainda não foi configurada corretamente.",
          );
          return;
        }

        const today = new Date();
        today.setHours(12, 0, 0, 0);
        const deliveryStart = addBusinessDays(
          today,
          productionMin + transitMin,
        );
        const deliveryEnd = addBusinessDays(
          today,
          Math.max(
            productionMin + transitMin,
            productionMax + transitMax,
          ),
        );
        const service = {
          name: `${this.dataset.serviceName || "J&T Standard"} · ${address.state} · ${weightTier.label}`,
          displayPrice: new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(price),
          deliveryLabel: `estimativa de ${formatDateRange(deliveryStart, deliveryEnd)}`,
          notice: this.getDeliveryNotice(address, region),
        };

        this.message.textContent = `CEP reconhecido: ${destinationLabel(address)}. Peso considerado: ${formatWeight(weightGrams)}. Confira a estimativa abaixo; o valor final será confirmado no checkout.`;
        this.renderServices([service]);
      }

      getQuoteWeightGrams() {
        if (this.dataset.context === "cart") {
          return Number(this.dataset.cartWeightGrams || 0);
        }
        const unitWeight = Number(this.dataset.unitWeightGrams || 0);
        const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
        return unitWeight * quantity;
      }

      getDeliveryNotice(address, region) {
        if (region === "north") {
          return "Fora da área urbana da capital, a retirada em uma unidade pode ser necessária.";
        }
        if (region === "midwest" || region === "northeast") {
          return "Em cidades do interior e áreas rurais, a retirada em uma unidade pode ser necessária.";
        }
        return "Em áreas remotas, a retirada em uma unidade pode ser necessária.";
      }

      async loadQuote(address) {
        try {
          const payload = {
            address,
            context: this.dataset.context || "product",
          };
          if (this.dataset.productId)
            payload.productId = this.dataset.productId;
          if (this.dataset.variantId)
            payload.variantId = this.dataset.variantId;
          if (payload.context === "cart") {
            const cartResponse = await fetch(
              `${window.routes?.cart_url || "/cart"}.js`,
            );
            if (cartResponse.ok) payload.cart = await cartResponse.json();
          }

          const response = await fetchWithTimeout(
            this.dataset.quoteEndpoint,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            },
            12000,
          );
          if (!response.ok) throw new Error("quote-request-failed");
          const result = await response.json();
          const services = Array.isArray(result.services)
            ? result.services
            : [];
          if (!result.deliverable || services.length === 0) {
            this.showError(
              "Ainda não temos uma opção de entrega para este CEP.",
            );
            return;
          }
          this.renderServices(services);
        } catch (error) {
          this.message.textContent = `CEP reconhecido: ${destinationLabel(address)}. O frete será confirmado no checkout.`;
        }
      }

      renderServices(services) {
        if (!this.quote) return;
        this.quote.replaceChildren();
        services.slice(0, 3).forEach((service) => {
          const row = document.createElement("div");
          row.className = "pra-v2-cep__service";
          const details = document.createElement("div");
          details.className = "pra-v2-cep__service-details";
          const label = document.createElement("span");
          label.textContent = [service.name, service.deliveryLabel]
            .filter(Boolean)
            .join(" · ");
          details.append(label);
          if (service.notice) {
            const notice = document.createElement("small");
            notice.textContent = service.notice;
            details.append(notice);
          }
          const price = document.createElement("strong");
          price.textContent = service.displayPrice || "A confirmar";
          row.append(details, price);
          this.quote.append(row);
        });
        this.quote.hidden = false;
        const first = services[0];
        if (this.dataset.context === "cart") {
          updateCartSummaries(readAddress(), first);
        }
      }

      showError(message) {
        this.dataset.state = "error";
        this.input.setAttribute("aria-invalid", "true");
        this.message.hidden = false;
        this.message.textContent = message;
        if (this.quote) this.quote.hidden = true;
        if (this.dataset.context === "cart") {
          const summary = this.closest("[data-pra-cart-shipping]");
          const value = summary?.querySelector("[data-pra-shipping-value]");
          if (value) value.textContent = "Frete não configurado";
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.isLoading = isLoading;
        this.updateSubmitState();
        this.submitButton.textContent = isLoading ? "Consultando…" : "Calcular";
      }
    }

    customElements.define("pra-cep-calculator", PraCepCalculator);
  }

  const boot = () => {
    const address = readAddress();
    applyCheckoutAddress(address);
    updateCartSummaries(address);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('#checkout, #CartDrawer-Checkout');
    if (!button || button.disabled || event.defaultPrevented) return;
    const address = readAddress();
    if (!address) return;

    const params = new URLSearchParams();
    params.set('checkout[shipping_address][country]', 'BR');
    params.set('checkout[shipping_address][zip]', formatCep(address.cep));
    if (address.city) params.set('checkout[shipping_address][city]', address.city);
    if (address.state) params.set('checkout[shipping_address][province]', address.state);
    event.preventDefault();
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(`${root}cart/checkout?${params.toString()}`);
  });

  document.addEventListener(UPDATE_EVENT, (event) => {
    applyCheckoutAddress(event.detail);
    updateCartSummaries(event.detail);
  });
  document.addEventListener("DOMContentLoaded", boot, { once: true });
  document.addEventListener("shopify:section:load", boot);
})();
